import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Palette, Upload, Save, RotateCcw, Wand2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function BrandingSettings() {
  const { refreshBranding } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [branding, setBranding] = useState({
    brand_name: 'Life Architect',
    logo_url: '',
    favicon_url: '',
    primary_color: '#D4A574',
    secondary_color: '#C59D52',
    background_color: '#1a1816',
    text_color: '#FFFFFF',
    email_from_name: ''
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/branding`);
      // Ensure all fields have values (not null/undefined)
      setBranding({
        brand_name: response.data.brand_name || 'Life Architect',
        logo_url: response.data.logo_url || '',
        favicon_url: response.data.favicon_url || '',
        primary_color: response.data.primary_color || '#D4A574',
        secondary_color: response.data.secondary_color || '#C59D52',
        background_color: response.data.background_color || '#1a1816',
        text_color: response.data.text_color || '#FFFFFF',
        email_from_name: response.data.email_from_name || ''
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching branding:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/branding`, branding);
      // Refresh branding in theme context to apply changes immediately
      await refreshBranding();
      toast.success('Branding settings saved successfully');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchBranding();
    setHasChanges(false);
  };

  const handleExtractColors = async (source) => {
    try {
      setExtracting(true);
      const response = await axios.post(`${API_URL}/branding/extract-colors`, {
        source: source,
        type: source.startsWith('http') ? 'url' : 'logo'
      });

      if (response.data.colors) {
        // Apply extracted colors
        setBranding(prev => ({
          ...prev,
          primary_color: response.data.colors.primary || prev.primary_color,
          secondary_color: response.data.colors.secondary || prev.secondary_color,
          background_color: response.data.colors.background || prev.background_color,
          text_color: response.data.colors.text || prev.text_color
        }));
        setHasChanges(true);
        toast.success('Colors extracted successfully! Review and adjust as needed.');
      }
    } catch (error) {
      console.error('Error extracting colors:', error);
      toast.error('Failed to extract colors. Please try again or set them manually.');
    } finally {
      setExtracting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3a352f] border-t-[#C59D52]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Branding Settings</h2>
        <p className="text-gray-400">Customize how your coaching brand appears to clients</p>
      </div>

      {/* Auto Extract Colors Section */}
      <div className="bg-[#2a2520] rounded-lg border border-[#3a352f] p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-[#D4A574]" />
          Auto-Extract Colors
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          Automatically extract your brand colors from your logo or website. This will analyze your visual identity and suggest a color scheme.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              From Logo URL
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                placeholder="https://example.com/logo.png"
                id="extract-logo-url"
                className="flex-1 bg-[#1a1816] text-white px-4 py-2 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50"
              />
              <button
                type="button"
                onClick={() => {
                  const url = document.getElementById('extract-logo-url').value;
                  if (url) handleExtractColors(url);
                  else toast.error('Please enter a logo URL');
                }}
                disabled={extracting}
                className="px-4 py-2 bg-[#D4A574] hover:bg-[#C59D52] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {extracting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" />
                    Extract
                  </>
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              From Website
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                placeholder="https://yourwebsite.com"
                id="extract-website-url"
                className="flex-1 bg-[#1a1816] text-white px-4 py-2 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50"
              />
              <button
                type="button"
                onClick={() => {
                  const url = document.getElementById('extract-website-url').value;
                  if (url) handleExtractColors(url);
                  else toast.error('Please enter a website URL');
                }}
                disabled={extracting}
                className="px-4 py-2 bg-[#D4A574] hover:bg-[#C59D52] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {extracting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" />
                    Extract
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Identity Section */}
      <div className="bg-[#2a2520] rounded-lg border border-[#3a352f] p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Palette className="w-5 h-5 text-[#D4A574]" />
          Brand Identity
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Brand Name
            </label>
            <input
              type="text"
              value={branding.brand_name}
              onChange={(e) => handleChange('brand_name', e.target.value)}
              placeholder="Life Architect"
              className="w-full bg-[#1a1816] text-white px-4 py-2 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50"
            />
            <p className="text-xs text-gray-500 mt-1">This name will appear throughout the client dashboard</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Logo URL
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={branding.logo_url}
                onChange={(e) => handleChange('logo_url', e.target.value)}
                placeholder="https://example.com/logo.png"
                className="flex-1 bg-[#1a1816] text-white px-4 py-2 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50"
              />
              <button
                type="button"
                className="px-4 py-2 bg-[#3a352f] hover:bg-[#4a453f] text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Recommended size: 200x50px (PNG or SVG)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Favicon URL
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={branding.favicon_url}
                onChange={(e) => handleChange('favicon_url', e.target.value)}
                placeholder="https://example.com/favicon.ico"
                className="flex-1 bg-[#1a1816] text-white px-4 py-2 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50"
              />
              <button
                type="button"
                className="px-4 py-2 bg-[#3a352f] hover:bg-[#4a453f] text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Recommended size: 32x32px (ICO or PNG)</p>
          </div>
        </div>
      </div>

      {/* Color Scheme Section */}
      <div className="bg-[#2a2520] rounded-lg border border-[#3a352f] p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Color Scheme</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Primary Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={branding.primary_color}
                onChange={(e) => handleChange('primary_color', e.target.value)}
                className="w-16 h-10 rounded cursor-pointer border border-[#3a352f]"
              />
              <input
                type="text"
                value={branding.primary_color}
                onChange={(e) => handleChange('primary_color', e.target.value)}
                placeholder="#D4A574"
                className="flex-1 bg-[#1a1816] text-white px-4 py-2 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 font-mono"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Main accent color for buttons and highlights</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Secondary Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={branding.secondary_color}
                onChange={(e) => handleChange('secondary_color', e.target.value)}
                className="w-16 h-10 rounded cursor-pointer border border-[#3a352f]"
              />
              <input
                type="text"
                value={branding.secondary_color}
                onChange={(e) => handleChange('secondary_color', e.target.value)}
                placeholder="#C59D52"
                className="flex-1 bg-[#1a1816] text-white px-4 py-2 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 font-mono"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Secondary accent color for hover states</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Background Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={branding.background_color}
                onChange={(e) => handleChange('background_color', e.target.value)}
                className="w-16 h-10 rounded cursor-pointer border border-[#3a352f]"
              />
              <input
                type="text"
                value={branding.background_color}
                onChange={(e) => handleChange('background_color', e.target.value)}
                placeholder="#1a1816"
                className="flex-1 bg-[#1a1816] text-white px-4 py-2 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 font-mono"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Main background color</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Text Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={branding.text_color}
                onChange={(e) => handleChange('text_color', e.target.value)}
                className="w-16 h-10 rounded cursor-pointer border border-[#3a352f]"
              />
              <input
                type="text"
                value={branding.text_color}
                onChange={(e) => handleChange('text_color', e.target.value)}
                placeholder="#FFFFFF"
                className="flex-1 bg-[#1a1816] text-white px-4 py-2 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50 font-mono"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Primary text color</p>
          </div>
        </div>
      </div>

      {/* Email Customization Section */}
      <div className="bg-[#2a2520] rounded-lg border border-[#3a352f] p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Email Customization</h3>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Email From Name
          </label>
          <input
            type="text"
            value={branding.email_from_name}
            onChange={(e) => handleChange('email_from_name', e.target.value)}
            placeholder="Your Coaching Business"
            className="w-full bg-[#1a1816] text-white px-4 py-2 rounded-lg border border-[#3a352f] focus:outline-none focus:ring-2 focus:ring-[#C59D52]/50"
          />
          <p className="text-xs text-gray-500 mt-1">Name that appears in invitation emails</p>
        </div>
      </div>

      {/* Preview Section */}
      <div className="bg-[#2a2520] rounded-lg border border-[#3a352f] p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Preview</h3>

        <div className="bg-[#1a1816] rounded-lg p-6 border border-[#3a352f]">
          <div className="flex items-center gap-3 mb-4">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-8 object-contain" />
            ) : (
              <div className="text-lg font-bold" style={{ color: branding.primary_color }}>
                {branding.brand_name}
              </div>
            )}
          </div>

          <button
            type="button"
            style={{ backgroundColor: branding.primary_color }}
            className="px-6 py-2 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
          >
            Sample Button
          </button>

          <p className="mt-4" style={{ color: branding.text_color }}>
            This is how text will appear in your client dashboard
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end">
        <button
          onClick={handleReset}
          disabled={!hasChanges || saving}
          className="px-6 py-2 bg-[#3a352f] hover:bg-[#4a453f] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          Reset Changes
        </button>

        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="px-6 py-2 bg-[#D4A574] hover:bg-[#C59D52] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default BrandingSettings;
